import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import type { ClinicProfile, Env, Equipment, Phrase, Professional, ReportTemplate, TemplateTopic, User } from '../api'
import { MediaImage } from '../components/MediaImage'

// Configurações: modelos de laudo, frases-padrão, médicos e usuários.
export function Config({ user, env }: { user: User; env: Env | null }) {
  const [tab, setTab] = useState<'users' | 'models' | 'phrases' | 'doctors' | 'equipments' | 'clinic'>('models')
  const isAdmin = user.role === 'admin'
  // No aplicativo as contas são só para consulta: quem as cria é o painel online.
  const usersOnline = env?.mode === 'node'

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)' }}>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'stretch', padding: '0 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
        {([
          ['models', 'Modelos de Laudo'],
          ['phrases', 'Frases-padrão'],
          ['doctors', 'Médicos'],
          ['equipments', 'Aparelhos'],
          ['clinic', 'Clínica'],
          ['users', 'Usuários e Permissões'],
        ] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{
              padding: '9px 16px', border: 'none', borderRight: '1px solid var(--border)',
              font: '600 12px/1 var(--font-sans)', cursor: 'pointer',
              background: tab === id ? 'var(--surface)' : 'transparent',
            }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'models' && <Models isAdmin={isAdmin} />}
      {tab === 'phrases' && <Phrases />}
      {tab === 'doctors' && <Doctors isAdmin={isAdmin} canLink={isAdmin && !usersOnline} />}
      {tab === 'equipments' && <Equipments isAdmin={isAdmin} />}
      {tab === 'clinic' && <Clinic isAdmin={isAdmin} />}
      {tab === 'users' && <Users isAdmin={isAdmin && !usersOnline} />}
    </div>
  )
}

// ─────────────────────────── Modelos ───────────────────────────

function Models({ isAdmin }: { isAdmin: boolean }) {
  const [items, setItems] = useState<ReportTemplate[]>([])
  const [selected, setSelected] = useState<ReportTemplate | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    void api.templates.list(true).then((out) => {
      setItems(out.items)
      if (out.items[0]) setSelected(structuredClone(out.items[0]))
    }).catch(() => setError('falha ao carregar modelos'))
  }, [])

  async function createTemplate(from?: ReportTemplate) {
    setError('')
    setNotice('')
    try {
      const base: Partial<ReportTemplate> = from
        ? {
            name: `${from.name} 02`,
            printTitle: from.printTitle,
            equipment: from.equipment,
            active: true,
            topics: structuredClone(from.topics),
          }
        : {
            name: 'Novo modelo',
            printTitle: 'NOVO EXAME',
            equipment: '',
            active: true,
            topics: [
              { id: 'ind', title: 'Indicação', default: '' },
              { id: 'sed', title: 'Sedação', default: '' },
              { id: 'desc', title: 'Descrição', default: '' },
              { id: 'proc', title: 'Procedimentos', default: '' },
              { id: 'conc', title: 'Conclusão', default: '' },
            ],
          }
      const created = await api.templates.create(base)
      const list = [...items, created].sort((a, b) => a.name.localeCompare(b.name))
      setItems(list)
      setSelected(structuredClone(created))
      setNotice(from ? `Modelo duplicado de “${from.name}”.` : 'Modelo criado — ajuste os tópicos e grave.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao criar modelo')
    }
  }

  async function save() {
    if (!selected) return
    setError('')
    setNotice('')
    try {
      const saved = await api.templates.update(selected)
      setItems((list) => list.map((t) => (t.id === saved.id ? saved : t)))
      setSelected(structuredClone(saved))
      setNotice('Modelo gravado.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao gravar modelo')
    }
  }

  function updateTopic(i: number, patch: Partial<TemplateTopic>) {
    if (!selected) return
    const topics = selected.topics.map((t, idx) => (idx === i ? { ...t, ...patch } : t))
    setSelected({ ...selected, topics })
  }

  function addTopic() {
    if (!selected) return
    const id = `t${selected.topics.length + 1}_${Math.random().toString(36).slice(2, 6)}`
    setSelected({ ...selected, topics: [...selected.topics, { id, title: 'Novo tópico', default: '' }] })
  }

  function removeTopic(i: number) {
    if (!selected) return
    setSelected({ ...selected, topics: selected.topics.filter((_, idx) => idx !== i) })
  }

  function moveTopic(i: number, dir: -1 | 1) {
    if (!selected) return
    const topics = [...selected.topics]
    const j = i + dir
    if (j < 0 || j >= topics.length) return
    ;[topics[i], topics[j]] = [topics[j], topics[i]]
    setSelected({ ...selected, topics })
  }

  return (
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '270px 1fr', minHeight: 0 }}>
      <div style={{ borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 'none', padding: '9px 12px', borderBottom: '1px solid var(--border)', font: '600 10.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
          Modelos cadastrados
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {items.map((t) => (
            <button key={t.id} onClick={() => { setSelected(structuredClone(t)); setNotice('') }}
              style={{
                width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
                background: selected?.id === t.id ? 'var(--accent-soft)' : 'transparent',
              }}>
              <div style={{ font: '500 12.5px/1.3 var(--font-sans)' }}>{t.name}</div>
              <div style={{ marginTop: 2, font: '400 10.5px/1.3 var(--font-mono)', color: 'var(--text-2)' }}>
                {t.topics.length} tópicos · {t.active ? 'ativo' : 'inativo'}
              </div>
            </button>
          ))}
        </div>
        {isAdmin && (
          <div style={{ flex: 'none', padding: 9, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-primary" onClick={() => void createTemplate()} style={{ width: '100%', height: 32 }}>
              + Novo modelo
            </button>
          </div>
        )}
      </div>

      <div style={{ overflowY: 'auto', minHeight: 0, padding: '14px 18px' }}>
        {selected ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ font: '600 15px/1.2 var(--font-sans)' }}>{selected.name}</div>
              <div style={{ flex: 1 }} />
              {isAdmin && (
                <button className="btn" onClick={() => void createTemplate(selected)} style={{ height: 29 }} title="Cria um modelo novo com os mesmos tópicos">
                  Duplicar
                </button>
              )}
              <button className="btn" onClick={() => setSelected({ ...selected, active: !selected.active })} disabled={!isAdmin} style={{ height: 29 }}>
                {selected.active ? 'Desativar' : 'Ativar'}
              </button>
              <button className="btn btn-primary" onClick={() => void save()} disabled={!isAdmin} style={{ height: 29, padding: '0 16px' }}>
                Gravar
              </button>
            </div>

            {error && <div style={{ marginTop: 10, padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
            {notice && <div style={{ marginTop: 10, padding: '8px 11px', border: '1px solid var(--ok)', borderRadius: 6, background: 'var(--ok-soft)', fontSize: 12 }}>{notice}</div>}
            {!isAdmin && <div style={{ marginTop: 10, font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>Somente administradores editam modelos.</div>}

            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <label className="field"><span>Nome do modelo</span>
                <input className="input" value={selected.name} disabled={!isAdmin}
                  onChange={(e) => setSelected({ ...selected, name: e.target.value })} />
              </label>
              <label className="field"><span>Título impresso</span>
                <input className="input" value={selected.printTitle} disabled={!isAdmin}
                  onChange={(e) => setSelected({ ...selected, printTitle: e.target.value })} style={{ fontFamily: 'var(--font-mono)' }} />
              </label>
              <label className="field"><span>Equipamento padrão</span>
                <input className="input" value={selected.equipment ?? ''} disabled={!isAdmin}
                  onChange={(e) => setSelected({ ...selected, equipment: e.target.value })} />
              </label>
            </div>

            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ font: '600 11.5px/1 var(--font-sans)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Tópicos do modelo</span>
            </div>

            <div style={{ marginTop: 8, maxWidth: 780, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
              {selected.topics.map((t, i) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ font: '400 12px/1 var(--font-mono)', color: 'var(--text-3)' }}>⋮⋮</span>
                  <span style={{ font: '400 10.5px/1 var(--font-mono)', color: 'var(--text-3)', width: 18 }}>{String(i + 1).padStart(2, '0')}</span>
                  <input className="input" value={t.title} disabled={!isAdmin} onChange={(e) => updateTopic(i, { title: e.target.value })} style={{ width: 180 }} />
                  <input className="input" value={t.default ?? ''} disabled={!isAdmin} placeholder="texto padrão (opcional)"
                    onChange={(e) => updateTopic(i, { default: e.target.value })} style={{ flex: 1, fontFamily: 'var(--font-serif)' }} />
                  <button className="btn" onClick={() => moveTopic(i, -1)} disabled={!isAdmin || i === 0} style={{ width: 26, height: 24, padding: 0 }}>↑</button>
                  <button className="btn" onClick={() => moveTopic(i, 1)} disabled={!isAdmin || i === selected.topics.length - 1} style={{ width: 26, height: 24, padding: 0 }}>↓</button>
                  <button className="btn" onClick={() => removeTopic(i)} disabled={!isAdmin} style={{ height: 24, color: 'var(--danger)' }}>Excluir</button>
                </div>
              ))}
              <button className="btn" onClick={addTopic} disabled={!isAdmin}
                style={{ width: '100%', borderRadius: 0, border: 'none', background: 'var(--surface-2)', color: 'var(--primary)', height: 34, textAlign: 'left', padding: '0 11px' }}>
                + Adicionar tópico
              </button>
            </div>
          </>
        ) : (
          <div style={{ font: '400 12px/1.6 var(--font-mono)', color: 'var(--text-3)' }}>
            Selecione um modelo na lista ou crie um novo com “+ Novo modelo”.
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────── Frases ───────────────────────────

function Phrases() {
  const [topic, setTopic] = useState('desc')
  const [items, setItems] = useState<Phrase[]>([])
  const [text, setText] = useState('')
  const [error, setError] = useState('')

  async function load(t: string) {
    try {
      const out = await api.phrases.list(t)
      setItems(out.items)
    } catch {
      setError('falha ao carregar frases')
    }
  }
  useEffect(() => { void load(topic) }, [topic])

  async function add() {
    if (!text.trim()) return
    try {
      await api.phrases.create({ topic, text: text.trim() })
      setText('')
      await load(topic)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao criar frase')
    }
  }

  async function toggle(p: Phrase) {
    try {
      const updated = await api.phrases.update({ ...p, favorite: !p.favorite })
      setItems((list) => list.map((x) => (x.id === p.id ? updated : x)))
    } catch {
      /* silencioso */
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 18px', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ font: '600 13px/1 var(--font-sans)' }}>Frases por tópico</span>
        <input className="input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="id do tópico (ex.: desc, conc)" style={{ width: 220, fontFamily: 'var(--font-mono)' }} />
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <input className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Nova frase-padrão para este tópico…" style={{ flex: 1, fontFamily: 'var(--font-serif)' }} />
        <button className="btn btn-primary" onClick={() => void add()} style={{ height: 30, padding: '0 16px' }}>Adicionar</button>
      </div>
      {error && <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12 }}>{error}</div>}

      <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
        {items.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)' }}>
            <button onClick={() => void toggle(p)}
              style={{ width: 20, height: 20, border: 'none', background: 'transparent', color: p.favorite ? 'var(--accent)' : 'var(--text-3)', fontSize: 14, cursor: 'pointer', padding: 0 }}>
              {p.favorite ? '★' : '☆'}
            </button>
            <span style={{ flex: 1, font: '400 12.5px/1.4 var(--font-serif)' }}>{p.text}</span>
            <span style={{ font: '400 10px/1 var(--font-mono)', color: 'var(--text-3)' }}>{p.topic}</span>
          </div>
        ))}
        {items.length === 0 && (
          <div style={{ padding: 18, textAlign: 'center', font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
            Nenhuma frase para o tópico “{topic}”.
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────── Médicos ───────────────────────────

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']

const DOCTOR_COLS = '1.4fr 150px 1.1fr 170px 150px'
const NO_DOCTOR = { id: '', name: '', crm: '', crm_uf: '', specialty: '' }

// Médicos da clínica: são eles que aparecem como examinador no laudo.
// canLink: no painel online o administrador diz qual login é de cada médico.
function Doctors({ isAdmin, canLink }: { isAdmin: boolean; canLink: boolean }) {
  const [items, setItems] = useState<Professional[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [form, setForm] = useState(NO_DOCTOR)
  const [confirming, setConfirming] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    try {
      const out = await api.catalog.professionals()
      setItems(out.items)
      if (isAdmin) setUsers((await api.users.list()).items)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar médicos')
    } finally {
      setLoaded(true)
    }
  }
  useEffect(() => { void load() }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      const current = items.find((p) => p.id === form.id)
      await api.catalog.upsert('professionals', { ...current, ...form, id: form.id || undefined })
      setForm(NO_DOCTOR)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao gravar médico')
    }
  }

  async function remove(p: Professional) {
    setError('')
    setConfirming('')
    try {
      await api.catalog.remove('professionals', p.id)
      if (form.id === p.id) setForm(NO_DOCTOR)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao excluir médico')
    }
  }

  // um médico, um login: escolher outro usuário solta o anterior
  async function link(p: Professional, userId: string) {
    setError('')
    try {
      for (const u of users) {
        if (u.professionalId === p.id && u.id !== userId) await api.users.update(u.id, { professionalId: '' })
      }
      if (userId) await api.users.update(userId, { professionalId: p.id })
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao vincular o usuário')
    }
  }

  const editing = form.id !== ''

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 18px', maxWidth: 1000 }}>
      {error && <div style={{ marginBottom: 10, padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      {isAdmin && (
        <form onSubmit={save} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field"><span>Nome</span>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={{ width: 250 }} />
          </label>
          <label className="field"><span>CRM</span>
            <input className="input" value={form.crm} onChange={(e) => setForm({ ...form, crm: e.target.value })} style={{ width: 110, fontFamily: 'var(--font-mono)' }} />
          </label>
          <label className="field"><span>UF</span>
            <select className="input" value={form.crm_uf} onChange={(e) => setForm({ ...form, crm_uf: e.target.value })} required={form.crm.trim() !== ''} style={{ width: 70 }}>
              <option value="" />
              {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </label>
          <label className="field"><span>Especialidade</span>
            <input className="input" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} style={{ width: 210 }} />
          </label>
          <button className="btn btn-primary" type="submit" style={{ height: 30, padding: '0 16px' }}>{editing ? 'Gravar' : 'Novo médico'}</button>
          {editing && <button className="btn" type="button" onClick={() => setForm(NO_DOCTOR)} style={{ height: 30 }}>Cancelar</button>}
        </form>
      )}

      <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: DOCTOR_COLS, columnGap: 12, padding: '7px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <div>Nome</div><div>CRM</div><div>Especialidade</div><div>{isAdmin ? 'Usuário' : ''}</div><div>{isAdmin ? 'Ações' : ''}</div>
        </div>
        {items.map((p) => {
          const linked = users.find((u) => u.professionalId === p.id)
          return (
            <div key={p.id} style={{ display: 'grid', gridTemplateColumns: DOCTOR_COLS, columnGap: 12, padding: '8px 12px', borderBottom: '1px solid var(--border)', font: '400 12px/1.3 var(--font-sans)', alignItems: 'center', background: form.id === p.id ? 'var(--accent-soft)' : 'transparent' }}>
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
              <div style={{ fontFamily: 'var(--font-mono)' }}>{p.crm ? `${p.crm_uf} ${p.crm}` : '—'}</div>
              <div style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.specialty || '—'}</div>
              <div>
                {canLink ? (
                  <select className="input" value={linked?.id ?? ''} onChange={(e) => void link(p, e.target.value)} style={{ width: 150, height: 26, fontFamily: 'var(--font-mono)' }}>
                    <option value="">—</option>
                    {users.filter((u) => u.active || u.id === linked?.id).map((u) => <option key={u.id} value={u.id}>{u.login}</option>)}
                  </select>
                ) : (
                  isAdmin && <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>{linked?.login ?? '—'}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {isAdmin && (confirming === p.id ? (
                  <>
                    <button className="btn" onClick={() => void remove(p)} style={{ height: 24, fontSize: 11, color: 'var(--danger)', borderColor: 'var(--danger)' }}>Excluir</button>
                    <button className="btn" onClick={() => setConfirming('')} style={{ height: 24, fontSize: 11 }}>Cancelar</button>
                  </>
                ) : (
                  <>
                    <button className="btn" onClick={() => { setError(''); setForm({ id: p.id, name: p.name, crm: p.crm ?? '', crm_uf: p.crm_uf ?? '', specialty: p.specialty ?? '' }) }} style={{ height: 24, fontSize: 11 }}>Editar</button>
                    <button className="btn" onClick={() => setConfirming(p.id)} style={{ height: 24, fontSize: 11 }}>Excluir</button>
                  </>
                ))}
              </div>
            </div>
          )
        })}
        {loaded && items.length === 0 && (
          <div style={{ padding: 18, textAlign: 'center', font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
            Nenhum médico
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────── Aparelhos ───────────────────────────

const EQUIPMENT_COLS = '1.4fr 1fr 1fr 150px 150px'
const NO_EQUIPMENT = { id: '', description: '', brand: '', model: '', serial: '' }

// Aparelhos da clínica: o exame registra em qual foi feito, e ele sai no laudo.
function Equipments({ isAdmin }: { isAdmin: boolean }) {
  const [items, setItems] = useState<Equipment[]>([])
  const [form, setForm] = useState(NO_EQUIPMENT)
  const [confirming, setConfirming] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    try {
      setItems((await api.catalog.equipments()).items)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar aparelhos')
    } finally {
      setLoaded(true)
    }
  }
  useEffect(() => { void load() }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      const current = items.find((x) => x.id === form.id)
      await api.catalog.upsert('equipments', { ...current, ...form, id: form.id || undefined })
      setForm(NO_EQUIPMENT)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao gravar aparelho')
    }
  }

  async function remove(x: Equipment) {
    setError('')
    setConfirming('')
    try {
      await api.catalog.remove('equipments', x.id)
      if (form.id === x.id) setForm(NO_EQUIPMENT)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao excluir aparelho')
    }
  }

  const editing = form.id !== ''

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 18px', maxWidth: 1000 }}>
      {error && <div style={{ marginBottom: 10, padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      {isAdmin && (
        <form onSubmit={save} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field"><span>Nome</span>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required style={{ width: 230 }} />
          </label>
          <label className="field"><span>Marca</span>
            <input className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} style={{ width: 140 }} />
          </label>
          <label className="field"><span>Modelo</span>
            <input className="input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} style={{ width: 140 }} />
          </label>
          <label className="field"><span>Nº de série</span>
            <input className="input" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} style={{ width: 150, fontFamily: 'var(--font-mono)' }} />
          </label>
          <button className="btn btn-primary" type="submit" style={{ height: 30, padding: '0 16px' }}>{editing ? 'Gravar' : 'Novo aparelho'}</button>
          {editing && <button className="btn" type="button" onClick={() => setForm(NO_EQUIPMENT)} style={{ height: 30 }}>Cancelar</button>}
        </form>
      )}

      <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: EQUIPMENT_COLS, columnGap: 12, padding: '7px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <div>Nome</div><div>Marca</div><div>Modelo</div><div>Nº de série</div><div>{isAdmin ? 'Ações' : ''}</div>
        </div>
        {items.map((x) => (
          <div key={x.id} style={{ display: 'grid', gridTemplateColumns: EQUIPMENT_COLS, columnGap: 12, padding: '8px 12px', borderBottom: '1px solid var(--border)', font: '400 12px/1.3 var(--font-sans)', alignItems: 'center', background: form.id === x.id ? 'var(--accent-soft)' : 'transparent' }}>
            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.description}</div>
            <div style={{ color: 'var(--text-2)' }}>{x.brand || '—'}</div>
            <div style={{ color: 'var(--text-2)' }}>{x.model || '—'}</div>
            <div style={{ fontFamily: 'var(--font-mono)' }}>{x.serial || '—'}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isAdmin && (confirming === x.id ? (
                <>
                  <button className="btn" onClick={() => void remove(x)} style={{ height: 24, fontSize: 11, color: 'var(--danger)', borderColor: 'var(--danger)' }}>Excluir</button>
                  <button className="btn" onClick={() => setConfirming('')} style={{ height: 24, fontSize: 11 }}>Cancelar</button>
                </>
              ) : (
                <>
                  <button className="btn" onClick={() => { setError(''); setForm({ id: x.id, description: x.description, brand: x.brand ?? '', model: x.model ?? '', serial: x.serial ?? '' }) }} style={{ height: 24, fontSize: 11 }}>Editar</button>
                  <button className="btn" onClick={() => setConfirming(x.id)} style={{ height: 24, fontSize: 11 }}>Excluir</button>
                </>
              ))}
            </div>
          </div>
        ))}
        {loaded && items.length === 0 && (
          <div style={{ padding: 18, textAlign: 'center', font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
            Nenhum aparelho
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────── Clínica ───────────────────────────

// Dados da clínica que saem no cabeçalho do laudo.
function Clinic({ isAdmin }: { isAdmin: boolean }) {
  const [saved, setSaved] = useState<ClinicProfile | null>(null)
  const [form, setForm] = useState({ address: '', phone: '', logoSha: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void api.clinic.get()
      .then((p) => { setSaved(p); setForm({ address: p.address, phone: p.phone, logoSha: p.logoSha }) })
      .catch(() => setError('falha ao carregar os dados da clínica'))
  }, [])

  async function pickLogo(file: File | undefined) {
    if (!file) return
    setError('')
    if (file.size > 2 * 1024 * 1024) { setError('o logotipo precisa ter até 2 MB'); return }
    try {
      const media = await api.media.upload(file, { mime: file.type })
      setForm((f) => ({ ...f, logoSha: media.sha256 }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao enviar o logotipo')
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const p = await api.clinic.save(form)
      setSaved(p)
      setForm({ address: p.address, phone: p.phone, logoSha: p.logoSha })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao gravar os dados da clínica')
    } finally {
      setBusy(false)
    }
  }

  if (!saved) {
    return <div style={{ padding: '14px 18px', fontSize: 12, color: 'var(--danger)' }}>{error}</div>
  }
  const dirty = form.address !== saved.address || form.phone !== saved.phone || form.logoSha !== saved.logoSha

  return (
    <form onSubmit={save} style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 18px', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {error && <div style={{ padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      <div style={{ font: '600 15px/1.2 var(--font-sans)' }}>{saved.name}</div>

      <div className="field"><span>Logotipo</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 168, height: 60, border: '1px dashed var(--border-2)', borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {form.logoSha && <MediaImage sha={form.logoSha} alt="logotipo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          </div>
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden
                onChange={(e) => { void pickLogo(e.target.files?.[0]); e.target.value = '' }} />
              <button className="btn" type="button" onClick={() => fileRef.current?.click()} style={{ height: 30 }}>Escolher imagem</button>
              {form.logoSha && <button className="btn" type="button" onClick={() => setForm({ ...form, logoSha: '' })} style={{ height: 30 }}>Remover</button>}
            </>
          )}
        </div>
      </div>

      <label className="field"><span>Endereço</span>
        <input className="input" value={form.address} disabled={!isAdmin} maxLength={300} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </label>
      <label className="field"><span>Telefone</span>
        <input className="input" value={form.phone} disabled={!isAdmin} maxLength={80} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={{ width: 240 }} />
      </label>

      {isAdmin && (
        <div>
          <button className="btn btn-primary" type="submit" disabled={!dirty || busy} style={{ height: 30, padding: '0 18px' }}>Gravar</button>
        </div>
      )}
    </form>
  )
}

// ─────────────────────────── Usuários ───────────────────────────

const ROLES = [
  ['admin', 'Administrador'],
  ['medico', 'Médico'],
  ['tecnico', 'Técnico'],
  ['recepcao', 'Recepção'],
] as const

const PERMS = [
  { name: 'Administrador', lib: true, exc: true, fin: true, cfg: true },
  { name: 'Médico', lib: true, exc: false, fin: false, cfg: false },
  { name: 'Técnico', lib: false, exc: false, fin: false, cfg: false },
  { name: 'Recepção', lib: false, exc: false, fin: true, cfg: false },
]

function Users({ isAdmin }: { isAdmin: boolean }) {
  const [items, setItems] = useState<User[]>([])
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [form, setForm] = useState({ login: '', name: '', role: 'medico', password: '' })

  async function load() {
    try {
      const out = await api.users.list()
      setItems(out.items)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar usuários')
    }
  }
  useEffect(() => { void load() }, [])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    try {
      await api.users.create(form)
      setForm({ login: '', name: '', role: 'medico', password: '' })
      setNotice('Usuário criado.')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao criar usuário')
    }
  }

  async function toggleActive(u: User) {
    try {
      await api.users.update(u.id, { active: !u.active })
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao atualizar usuário')
    }
  }

  async function resetPassword(u: User) {
    const pwd = prompt(`Nova senha para ${u.login} (mínimo 8 caracteres):`)
    if (!pwd) return
    try {
      await api.users.update(u.id, { password: pwd })
      setNotice(`Senha de ${u.login} alterada.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao alterar senha')
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 18px', maxWidth: 1000 }}>
      {error && <div style={{ marginBottom: 10, padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
      {notice && <div style={{ marginBottom: 10, padding: '8px 11px', border: '1px solid var(--ok)', borderRadius: 6, background: 'var(--ok-soft)', fontSize: 12 }}>{notice}</div>}

      {isAdmin && (
        <form onSubmit={create} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field"><span>Login</span>
            <input className="input" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} required style={{ fontFamily: 'var(--font-mono)' }} />
          </label>
          <label className="field"><span>Nome</span>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label className="field"><span>Perfil</span>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="field"><span>Senha inicial</span>
            <input className="input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
          </label>
          <button className="btn btn-primary" type="submit" style={{ height: 30, padding: '0 16px' }}>Novo usuário</button>
        </form>
      )}

      <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '130px 1.2fr 130px 90px 150px', padding: '7px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <div>Login</div><div>Nome</div><div>Perfil</div><div>Ativo</div><div>Ações</div>
        </div>
        {items.map((u) => (
          <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '130px 1.2fr 130px 90px 150px', padding: '8px 12px', borderBottom: '1px solid var(--border)', font: '400 12px/1.3 var(--font-sans)', alignItems: 'center' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{u.login}</div>
            <div>{u.name}</div>
            <div style={{ color: 'var(--text-2)' }}>{ROLES.find(([v]) => v === u.role)?.[1] ?? u.role}</div>
            <div>
              <span className="badge" style={{ background: u.active ? 'var(--ok-soft)' : 'var(--danger-soft)', color: u.active ? 'var(--ok)' : 'var(--danger)' }}>
                {u.active ? 'ATIVO' : 'INATIVO'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isAdmin && (
                <>
                  <button className="btn" onClick={() => void toggleActive(u)} style={{ height: 24, fontSize: 11 }}>{u.active ? 'Desativar' : 'Ativar'}</button>
                  <button className="btn" onClick={() => void resetPassword(u)} style={{ height: 24, fontSize: 11 }}>Senha</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 18, font: '600 12.5px/1 var(--font-sans)' }}>Matriz de permissões</div>
      <div style={{ marginTop: 9, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', maxWidth: 720 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr repeat(4,1fr)', padding: '7px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <div>Perfil</div><div>Liberar laudo</div><div>Excluir laudo</div><div>Ver financeiro</div><div>Configurações</div>
        </div>
        {PERMS.map((p) => (
          <div key={p.name} style={{ display: 'grid', gridTemplateColumns: '1.4fr repeat(4,1fr)', padding: '9px 12px', borderBottom: '1px solid var(--border)', font: '400 12px/1 var(--font-sans)' }}>
            <div style={{ fontWeight: 500 }}>{p.name}</div>
            {[p.lib, p.exc, p.fin, p.cfg].map((v, i) => (
              <div key={i} style={{ color: v ? 'var(--ok)' : 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>{v ? '✓' : '—'}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
